import { useEffect } from 'react'
import type { ChangeEvent, KeyboardEvent } from 'react'

import type { Theme } from '@emotion/react'
import { useDebounce } from 'use-debounce'

import { CloseIcon, SearchIcon } from '@twistopayments/design-system/icons/inline'

import { IconButton } from '../../atoms/button'
import { Input } from '../../atoms/input'

import * as styles from './search-bar.styles'
import type { Sizes } from './search-bar.styles'

type Props = {
  searchQuery: string
  setSearchQuery: (newQuery: string) => void
  size?: Sizes
  name: string
  placeholder?: string
  disabled?: boolean
  autocomplete?: string
  onBlur?: () => void
  onFocus?: () => void
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
  /** Optional callback fired with the debounced search term — host app
   *  wires this to its own analytics if desired. */
  onDebouncedQuery?: (term: string) => void
}

const debounceTimer = 1500

export const SearchBar = ({
  searchQuery,
  setSearchQuery,
  size = 'large',
  name,
  placeholder,
  disabled = false,
  autocomplete,
  onBlur,
  onFocus,
  onKeyDown,
  onDebouncedQuery,
}: Props) => {
  const [debouncedSearchQuery] = useDebounce(searchQuery, debounceTimer)

  useEffect(() => {
    if (debouncedSearchQuery !== '') {
      onDebouncedQuery?.(debouncedSearchQuery)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery])

  return (
    <Input
      autoComplete={autocomplete}
      border={false}
      data-testid="search-bar"
      disabled={disabled}
      endAdornment={
        searchQuery ? (
          <IconButton
            color="neutral"
            icon={CloseIcon}
            size="small"
            onClick={() => setSearchQuery('')}
          />
        ) : undefined
      }
      name={name}
      placeholder={placeholder}
      startAdornment={<SearchIcon />}
      styles={{
        root: styles.inputWrapper,
        input: [
          styles.input,
          styles.sizes[size],
          disabled && styles.disabledInput,
        ],
        startAdornment: (theme: Theme) => [
          styles.inputIcon(theme, size),
          disabled && styles.disabledInputIcon,
        ],
        endAdornment: styles.endAdornment,
      }}
      value={searchQuery}
      onBlur={onBlur}
      onChange={(event: ChangeEvent<HTMLInputElement>) =>
        setSearchQuery(event.target.value)
      }
      onFocus={onFocus}
      onKeyDown={onKeyDown}
    />
  )
}
